import React, { useState } from 'react';
import { MapPin, Mail, Clock, Send, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { contactApi } from '../services/api';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await contactApi.submit(formData);
      toast.success('Message sent successfully!', {
        description: "We'll get back to you within 24 hours.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen bg-[#FDF6E3] pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#3D2314] to-[#5C3D2E] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-[#D4A853] font-medium text-sm tracking-widest uppercase">Get in Touch</span>
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#FDF6E3] mt-4 mb-6">
            Contact Us
          </h1>
          <p className="text-[#FDF6E3]/70 max-w-2xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-serif text-3xl font-bold text-[#5C3D2E] mb-8">
                Let's Start a Conversation
              </h2>
              <p className="text-[#5C3D2E]/70 mb-10 leading-relaxed">
                Whether you have questions about our products, need help with an order, 
                or want to discuss wholesale partnerships, we're here to help.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C17817]/10 flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-[#C17817]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#5C3D2E] mb-1">Email Us</h4>
                    <p className="text-[#5C3D2E]/60">hello@houseofbania.com</p>
                    <p className="text-[#5C3D2E]/60">support@houseofbania.com</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C17817]/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-[#C17817]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#5C3D2E] mb-1">Business Hours</h4>
                    <p className="text-[#5C3D2E]/60">Monday - Friday: 9AM - 6PM IST</p>
                    <p className="text-[#5C3D2E]/60">Saturday: 10AM - 4PM IST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#C17817]/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="h-5 w-5 text-[#C17817]" />
                  </div>
                  <div>
                    <h4 className="font-medium text-[#5C3D2E] mb-1">Manufacturing Unit</h4>
                    <p className="text-[#5C3D2E]/60">Rajasthan, India</p>
                  </div>
                </div>
              </div>

              {/* Shipping Regions */}
              <div className="mt-12 p-6 bg-white rounded-2xl border border-[#D4A853]/20">
                <h4 className="font-serif text-lg font-semibold text-[#5C3D2E] mb-4">
                  We Ship To
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { city: 'Singapore', flag: '🇸🇬' },
                    { city: 'Dubai, UAE', flag: '🇦🇪' },
                    { city: 'Australia', flag: '🇦🇺' },
                    { city: 'United Kingdom', flag: '🇬🇧' },
                    { city: 'India', flag: '🇮🇳' },
                  ].map((item) => (
                    <div key={item.city} className="flex items-center gap-2">
                      <span className="text-xl">{item.flag}</span>
                      <span className="text-[#5C3D2E]/70">{item.city}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-xl border border-[#D4A853]/10">
              <h3 className="font-serif text-2xl font-bold text-[#5C3D2E] mb-6">
                Send us a Message
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-[#5C3D2E] mb-2">
                    Your Name
                  </label>
                  <Input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="border-[#D4A853]/30 focus:border-[#C17817] focus:ring-[#C17817]/20"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#5C3D2E] mb-2">
                    Email Address
                  </label>
                  <Input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="border-[#D4A853]/30 focus:border-[#C17817] focus:ring-[#C17817]/20"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#5C3D2E] mb-2">
                    Subject
                  </label>
                  <Input
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="border-[#D4A853]/30 focus:border-[#C17817] focus:ring-[#C17817]/20"
                    placeholder="How can we help?"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#5C3D2E] mb-2">
                    Message
                  </label>
                  <Textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="border-[#D4A853]/30 focus:border-[#C17817] focus:ring-[#C17817]/20 resize-none"
                    placeholder="Tell us more about your inquiry..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#C17817] hover:bg-[#8B4513] text-[#FDF6E3] py-6 rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
